/* Admin · Share a Bureaucratic Library instrument to a customer.
   Search by Vinci Code (5151 series) or email → confirm → instant email
   with a unique fillable link + auto-minted VNC-SRN. */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Search, Send, Loader2, ShieldCheck, Hash, Mail, User, Copy,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BureaucraticShareDialog({ template, onClose, onShared }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/admin/bureaucratic/customers`, {
          params: { q: q.trim() }, withCredentials: true,
        });
        if (alive) setResults(data.items || []);
      } catch { if (alive) setResults([]); }
      finally { if (alive) setSearching(false); }
    }, 260);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const share = async () => {
    if (!picked) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/bureaucratic/shares`,
        { template_slug: template.slug, email: picked.email }, { withCredentials: true });
      setDone(data.share);
      toast.success(`Mandate dispatched · ${data.share.srn}`);
      onShared?.(data.share);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Share failed");
    } finally { setSending(false); }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        style={{ background: "rgba(15,17,20,0.72)", backdropFilter: "blur(10px)" }}
        onClick={onClose}
        data-testid="share-dialog-overlay"
      >
        <motion.div
          initial={{ y: 24, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 12, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-xl border shadow-2xl"
          style={{ background: "#F2F0EA", borderColor: "#4A5058" }}
          data-testid="share-dialog"
        >
          <div className="flex items-start justify-between px-6 py-5 border-b"
               style={{ background: "#1F2226", borderColor: "#4A5058" }}>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-[0.32em] text-[#7A8590]">
                Share to Customer · Rectification Mandate
              </div>
              <h2 className="font-serif text-lg text-[#E4E6E8] mt-1 leading-snug">{template.title}</h2>
            </div>
            <button onClick={onClose} data-testid="share-dialog-close"
                    className="text-[#C4C8CC] hover:text-white mt-1"><X size={16} /></button>
          </div>

          {done ? <Receipt share={done} onClose={onClose} /> : (
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#6B6F76]">
                  Search partner · Vinci Code (5151 series) or Email
                </span>
                <div className="mt-1.5 flex items-center gap-2 border bg-white/80 px-3 py-2"
                     style={{ borderColor: "#7A8590" }}>
                  <Search size={13} className="text-[#4A5058]" />
                  <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setPicked(null); }}
                         placeholder="VN-5151-0042  ·  partner@company.com"
                         data-testid="share-search-input"
                         className="flex-1 bg-transparent outline-none text-[13px] font-mono text-[#161A1E] placeholder:text-[#9AA0A6]" />
                  {searching && <Loader2 size={13} className="animate-spin text-[#7A8590]" />}
                </div>
              </label>

              <div className="max-h-64 overflow-y-auto border divide-y" style={{ borderColor: "#C4C8CC" }}
                   data-testid="share-search-results">
                {results.length === 0 ? (
                  <div className="px-4 py-6 text-center text-[11px] font-mono text-[#8A9098]">
                    {q.trim().length < 2 ? "Type at least two characters to search the 5151 registry."
                      : searching ? "Scanning registry…" : "No partner matches that code or email."}
                  </div>
                ) : results.map((u) => {
                  const active = picked?.email === u.email;
                  return (
                    <button key={u.email} onClick={() => setPicked(u)}
                            data-testid={`share-result-${u.email}`}
                            className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                              active ? "bg-[#1F2226] text-[#E4E6E8]" : "bg-white/70 hover:bg-[#E4E6E8] text-[#161A1E]"}`}>
                      <div className={`w-8 h-8 shrink-0 rounded-sm flex items-center justify-center border ${
                        active ? "border-[#D8B23E] text-[#D8B23E]" : "border-[#7A8590] text-[#4A5058]"}`}>
                        <User size={13} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-serif text-[14px] truncate">{u.name || u.company || u.email}</div>
                        <div className={`text-[10px] font-mono truncate ${active ? "text-[#C4C8CC]" : "text-[#6B6F76]"}`}>
                          {u.email}{u.company && u.name ? ` · ${u.company}` : ""}
                        </div>
                      </div>
                      <div className={`font-mono text-[10px] tracking-[0.18em] ${active ? "text-[#D8B23E]" : "text-[#2C3138]"}`}>
                        {u.vinci_code || "—"}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: "#C4C8CC" }}>
                <div className="text-[10px] font-mono text-[#6B6F76] uppercase tracking-[0.18em]">
                  {picked ? <>To · <span className="text-[#161A1E]">{picked.email}</span></> : "Select a partner to continue"}
                </div>
                <button onClick={share} disabled={!picked || sending}
                        data-testid="share-send-button"
                        className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] bg-[#1F2226] text-[#E4E6E8] hover:bg-[#2C3138] px-5 py-2.5 rounded-sm disabled:opacity-40 transition-colors">
                  {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                  Send Mandate
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Receipt({ share, onClose }) {
  const copy = () => { navigator.clipboard?.writeText(share.srn); toast.message("SRN copied"); };
  return (
    <div className="p-6" data-testid="share-receipt">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck size={22} className="text-[#2FB999]" />
        <div>
          <div className="font-serif text-lg text-[#161A1E]">Mandate Dispatched</div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#6B6F76]">
            {share.email_dispatched ? "Email delivered via Zoho SMTP" : "Email queued · check SMTP alerts"}
          </div>
        </div>
      </div>
      <div className="border bg-white/80 divide-y" style={{ borderColor: "#C4C8CC" }}>
        <Row icon={<Hash size={12} />} k="VNC-SRN" v={<span data-testid="share-receipt-srn" className="text-[#8A6D1F] font-bold tracking-[0.14em]">{share.srn}</span>}
             action={<button onClick={copy} data-testid="share-receipt-copy" className="text-[#4A5058] hover:text-black"><Copy size={12} /></button>} />
        <Row icon={<Mail size={12} />} k="Recipient" v={share.recipient_email} />
        <Row icon={<User size={12} />} k="Vinci Code" v={share.vinci_code || "—"} />
      </div>
      <div className="mt-5 flex justify-end">
        <button onClick={onClose} data-testid="share-receipt-done"
                className="font-mono text-[10px] uppercase tracking-[0.28em] border border-[#4A5058] text-[#2C3138] px-5 py-2 rounded-sm hover:bg-[#E4E6E8]">
          Done
        </button>
      </div>
    </div>
  );
}

function Row({ icon, k, v, action }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <span className="text-[#4A5058]">{icon}</span>
      <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#6B6F76] w-24">{k}</span>
      <span className="flex-1 font-mono text-[12px] text-[#161A1E] truncate">{v}</span>
      {action}
    </div>
  );
}
